import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { LogIn, UserCheck, X } from 'lucide-react';
import { usePermissions } from '../../hooks/use-permissions';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface SwitchableUser {
  id: string;
  name: string;
  email: string;
  roles: Array<{ role: { id: string; name: string } }>;
}

interface ImpersonateResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    permissions: string[];
    licensingAuthorities: string[];
    impersonatedBy: string;
  };
}

export function UserSwitcher() {
  const { can } = usePermissions();
  const { originalSession, startImpersonation, exitImpersonation, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const isEnabled = import.meta.env.VITE_ENABLE_USER_SWITCH === 'true';
  const hasPermission = can('user:impersonate');
  const isImpersonating = originalSession !== null;

  // All hooks must be called unconditionally — enabled flag prevents actual fetching
  const { data: switchableUsers = [] } = useQuery<SwitchableUser[]>({
    queryKey: ['switchable-users'],
    queryFn: () => api.get('auth/switchable-users').json<SwitchableUser[]>(),
    enabled: isEnabled && hasPermission && !isImpersonating,
    staleTime: 30_000,
  });

  // Always show when impersonating so the exit option is available
  if (!isEnabled || (!hasPermission && !isImpersonating)) return null;

  const handleSwitch = async (target: SwitchableUser) => {
    const res = await api.post(`auth/impersonate/${target.id}`).json<ImpersonateResponse>();
    startImpersonation(
      { id: res.user.id, email: res.user.email, name: res.user.name },
      res.user.permissions,
      res.accessToken,
      res.user.licensingAuthorities,
    );
    navigate({ to: location.pathname, search: location.search });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch user"
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-full border transition-colors lg:h-11 lg:w-11',
            isImpersonating
              ? 'border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700',
          )}
        >
          {isImpersonating ? <UserCheck size={18} /> : <LogIn size={18} />}
          {isImpersonating && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-400" />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="z-[10000] w-64" sideOffset={8}>
        {isImpersonating ? (
          <>
            <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-gray-400 px-2 py-1.5">
              Impersonating
            </DropdownMenuLabel>
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={exitImpersonation}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer text-error-600 focus:text-error-600"
            >
              <X size={14} />
              <span>Exit impersonation</span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-gray-400 px-2 py-1.5">
              Switch user
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {switchableUsers.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">No users available</div>
            ) : (
              switchableUsers.map((u) => (
                <DropdownMenuItem
                  key={u.id}
                  onClick={() => handleSwitch(u)}
                  className="flex flex-col items-start gap-0.5 px-3 py-2 cursor-pointer"
                >
                  <span className="text-sm font-medium text-gray-800">{u.name}</span>
                  <span className="text-xs text-gray-400">{u.email}</span>
                </DropdownMenuItem>
              ))
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
